import type { SupportedLocale } from './locales.ts';

/**
 * Time zones — P1-100 step 5.
 *
 * THE RULE: every date this product renders is formatted in an EXPLICIT IANA time
 * zone that travelled with the request. The browser's zone is never used unless
 * somebody wrote {@link browserTimeZone} on purpose, and there is no default.
 *
 * The legacy w2i-bridge did the opposite twice over. Report SQL carried
 * `AT TIME ZONE 'Europe/Kyiv'` and elsewhere a hardcoded `+02:00` offset. The
 * literal offset is right only from late October to late March: for roughly five
 * months a year every timestamp it produced was an hour off, so "conversations
 * opened today" silently included or dropped an hour of traffic at the boundary
 * and the daily report never matched the inbox. A named zone knows about DST; an
 * offset is a snapshot of one day's answer.
 *
 * Because the zone is a REQUEST property (the viewing agent's zone, or the
 * brand's business zone for a report), it is a value passed in, not a module
 * constant. {@link LocalizationContext} is the shape that carries it.
 */

declare const timeZoneBrand: unique symbol;

/**
 * An IANA zone identifier that has been checked against the runtime's tz
 * database. Only {@link resolveTimeZone} can produce one, so a formatter cannot
 * be handed a typo.
 */
export type TimeZone = string & { readonly [timeZoneBrand]: true };

/** Ukraine's civil time zone. Observes DST — see the note at the top of this file. */
export const UKRAINE_TIME_ZONE = 'Europe/Kyiv';

/** UTC. The right zone for machine-facing output (exports, audit logs), never for a UI. */
export const UTC_TIME_ZONE = 'UTC';

/**
 * ES2024 made `Intl` accept a numeric UTC offset ("+02:00") wherever a time zone
 * is expected, so the platform check alone would wave through precisely the value
 * that caused the legacy bug. An offset is rejected here BEFORE `Intl` sees it:
 * this package's contract is a zone that knows its own DST rules, and "+02:00"
 * is a fact about one day in Kyiv, not about Kyiv.
 */
const OFFSET_LIKE = /^[+-]/u;

export function isTimeZone(value: string): boolean {
  if (OFFSET_LIKE.test(value)) return false;

  try {
    // The only portable validity check: construction throws RangeError for an
    // unknown identifier. `Intl.supportedValuesOf('timeZone')` is not usable here
    // because it omits the deprecated-but-still-valid aliases (`Europe/Kiev`)
    // that legacy rows are full of.
    Intl.DateTimeFormat('en-US', { timeZone: value });
    return true;
  } catch (cause: unknown) {
    if (cause instanceof RangeError) return false;
    // Anything that is not "unknown zone" is a broken runtime, not a bad input.
    throw cause;
  }
}

export function resolveTimeZone(value: string): TimeZone {
  if (!isTimeZone(value)) {
    throw new RangeError(
      `unknown IANA time zone ${JSON.stringify(value)}; expected an identifier such as "Europe/Kyiv"`,
    );
  }
  return value as TimeZone;
}

/**
 * The zone the BROWSER is in. Exported so that choosing it is a visible decision
 * at the call site; nothing in this package calls it for you.
 */
export function browserTimeZone(): TimeZone {
  return resolveTimeZone(new Intl.DateTimeFormat().resolvedOptions().timeZone);
}

/**
 * The zone offset in effect at a given instant, in minutes east of UTC.
 *
 * Derived from the formatted `longOffset` name rather than from arithmetic on a
 * `Date`, because `Date` only knows the runtime's own zone.
 */
export function timeZoneOffsetMinutes(timeZone: TimeZone, at: Date): number {
  const formatter = new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'longOffset' });
  const part = formatter.formatToParts(at).find((candidate) => candidate.type === 'timeZoneName');
  if (part === undefined) {
    throw new TypeError(`runtime did not report an offset for ${timeZone}`);
  }
  // "GMT+03:00", or plain "GMT" at zero offset.
  const match = /^GMT(?<sign>[+-])(?<hours>\d{1,2}):(?<minutes>\d{2})$/u.exec(part.value);
  if (match?.groups === undefined) return 0;
  const sign = match.groups['sign'] === '-' ? -1 : 1;
  return sign * (Number(match.groups['hours']) * 60 + Number(match.groups['minutes']));
}

/**
 * The i18n half of a request's context: which language to render in and which
 * zone to render instants in.
 *
 * It is deliberately separate from `RequestContext` in `@axon/contracts`, which
 * carries tenancy and identity and has no time-zone field yet. Once P1-021 adds
 * one, this interface becomes a `Pick<>` of it and nothing else here changes.
 */
export interface LocalizationContext {
  readonly locale: SupportedLocale;
  readonly timeZone: TimeZone;
}
